import { ADMIN_NAV_SEARCH_ONLY, ADMIN_NAV_SECTIONS } from "./adminNavModel";
import type {
  AdminNavPage,
  AdminNavSection,
  AdminNavSectionId,
  AdminNavTab,
} from "./adminNavModel.types";
import {
  ADMIN_ROUTES,
  type AdminRouteAccessArgs,
  type AdminRouteDefinition,
  evaluateAdminRouteAccess,
  findAdminRouteByPathname,
  type SidebarNavigationItem,
} from "./adminRoutes";

/** Where one route sits in the navigation map. */
export interface AdminNavLocation {
  section: AdminNavSection;
  /** `null` for a search-only route such as Business types. */
  page: AdminNavPage | null;
  /** The tab the route is, when it is one of its page's own tabs. */
  tab: AdminNavTab | null;
  route: AdminRouteDefinition;
}

export interface AdminNavBreadcrumb {
  label: string;
  path?: string;
}

export interface AdminNavSearchEntry {
  id: string;
  label: string;
  /** Where it lives, e.g. "Administration › Audits". */
  location: string;
  path: string;
  routeId: string;
}

const getRoute = (routeId: string): AdminRouteDefinition | undefined =>
  ADMIN_ROUTES.find((route) => route.id === routeId);

/** Tabs with their own address, in order; in-page tabs share the page's. */
const getAddressedTabs = (page: AdminNavPage): AdminNavTab[] =>
  (page.tabs ?? []).filter((item) => !item.inPage);

/** Every route the page owns, landing first. */
export const getAdminNavPageRouteIds = (page: AdminNavPage): string[] =>
  Array.from(
    new Set([
      page.routeId,
      ...getAddressedTabs(page).map((item) => item.routeId),
      ...(page.fallbackRouteId ? [page.fallbackRouteId] : []),
      ...(page.aliasRouteIds ?? []),
      ...(page.memberRouteIds ?? []),
    ]),
  );

let locationIndex: Map<string, Omit<AdminNavLocation, "route">> | null = null;

const getLocationIndex = () => {
  if (locationIndex) return locationIndex;
  const index = new Map<string, Omit<AdminNavLocation, "route">>();
  for (const section of ADMIN_NAV_SECTIONS) {
    for (const page of section.pages) {
      for (const routeId of getAdminNavPageRouteIds(page)) {
        const tab =
          getAddressedTabs(page).find((item) => item.routeId === routeId) ??
          null;
        index.set(routeId, { section, page, tab });
      }
    }
  }
  for (const entry of ADMIN_NAV_SEARCH_ONLY) {
    const section = ADMIN_NAV_SECTIONS.find(
      (item) => item.id === entry.sectionId,
    );
    if (section) index.set(entry.routeId, { section, page: null, tab: null });
  }
  locationIndex = index;
  return index;
};

export const findAdminNavLocationForRoute = (
  route: AdminRouteDefinition,
): AdminNavLocation | null => {
  const location = getLocationIndex().get(route.id);
  return location ? { ...location, route } : null;
};

export const findAdminNavLocation = (
  pathname: string,
): AdminNavLocation | null => {
  const route = findAdminRouteByPathname(pathname);
  return route ? findAdminNavLocationForRoute(route) : null;
};

/** The query parameter naming the dashboard a shared workbench came from. */
export const ADMIN_NAV_ORIGIN_PARAM = "from";

const ADMIN_NAV_ORIGIN_PATHS: Readonly<Record<string, string>> = {
  users: "/users",
};
const ADMIN_NAV_ORIGIN_ROUTES: ReadonlySet<string> = new Set([
  "/crm/duplicates",
]);

/**
 * A shared workbench opened from a dashboard keeps that dashboard's place in
 * the navigation (C7-CT): Combine on Users opens Duplicate review with
 * `from=users`, so the sidebar and breadcrumb read Users, not All contacts.
 * Null when the page has no such origin.
 */
export const getAdminNavOriginPathname = (
  pathname: string,
  search: string,
): string | null => {
  if (!ADMIN_NAV_ORIGIN_ROUTES.has(pathname)) return null;
  const origin = new URLSearchParams(search).get(ADMIN_NAV_ORIGIN_PARAM);
  return origin ? (ADMIN_NAV_ORIGIN_PATHS[origin] ?? null) : null;
};

export const getAdminNavSectionIdForPathname = (
  pathname: string,
): AdminNavSectionId | null =>
  findAdminNavLocation(pathname)?.section.id ?? null;

const canOpen = (routeId: string, args?: AdminRouteAccessArgs) => {
  const route = getRoute(routeId);
  return route ? evaluateAdminRouteAccess(route, args).canAccess : false;
};

/**
 * The page's address for this admin: its own route, else its first tab they
 * may open, else its fallback (e.g. scoped Organisations). `undefined` when
 * none is open to them, so the page is not shown.
 */
export const getAdminNavPageLandingPath = (
  page: AdminNavPage,
  args?: AdminRouteAccessArgs,
): string | undefined => {
  const candidates = [
    page.routeId,
    ...getAddressedTabs(page).map((item) => item.routeId),
    ...(page.fallbackRouteId ? [page.fallbackRouteId] : []),
  ];
  const routeId = candidates.find((candidate) => canOpen(candidate, args));
  return routeId ? getRoute(routeId)?.path : undefined;
};

export const getAdminNavSectionLandingPath = (
  sectionId: AdminNavSectionId,
  args?: AdminRouteAccessArgs,
): string | undefined => {
  const section = ADMIN_NAV_SECTIONS.find((item) => item.id === sectionId);
  for (const page of section?.pages ?? []) {
    const path = getAdminNavPageLandingPath(page, args);
    if (path) return path;
  }
  return undefined;
};

const toPageItem = (
  page: AdminNavPage,
  args: AdminNavSidebarArgs | undefined,
): SidebarNavigationItem | null => {
  const route = getRoute(page.routeId);
  if (!route) return null;
  const landingPath = getAdminNavPageLandingPath(page, args);
  if (!landingPath && !args?.includeUnavailable) return null;
  const pagePaths = getAdminNavPageRouteIds(page).flatMap((routeId) => {
    const pageRoute = getRoute(routeId);
    return pageRoute ? [pageRoute.path] : [];
  });
  return {
    id: page.id,
    label: page.label,
    icon: route.icon,
    path: landingPath ?? route.path,
    activeMatchPaths: pagePaths.filter((path) => path !== landingPath),
    disabled: !landingPath,
    description: landingPath
      ? undefined
      : evaluateAdminRouteAccess(route, args).unavailableReason,
  };
};

export interface AdminNavSidebarArgs extends AdminRouteAccessArgs {
  /** Also list pages the admin cannot open, marked disabled. */
  includeUnavailable?: boolean;
  /** Only this section plus Dashboard (the legacy sidebar and the pills). */
  activeSectionId?: AdminNavSectionId | null;
}

/**
 * The sidebar: sections with their pages, two levels only (F-195). A section
 * with no page this admin may open is left out, so Sales only shows once one
 * of its pages is open to them. A section whose only page shares its name
 * (Dashboard) is a single row.
 */
export const getAdminNavSidebarItems = (
  args?: AdminNavSidebarArgs,
): SidebarNavigationItem[] =>
  ADMIN_NAV_SECTIONS.flatMap((section) => {
    // Filtering to one section still keeps the Dashboard row.
    if (
      args?.activeSectionId &&
      section.id !== args.activeSectionId &&
      section.id !== "dashboard"
    ) {
      return [];
    }
    const pages = section.pages.flatMap((page) => {
      const item = toPageItem(page, args);
      return item ? [item] : [];
    });
    if (pages.length === 0) return [];
    const [onlyPage] = pages;
    if (pages.length === 1 && onlyPage.label === section.label) {
      return [onlyPage];
    }
    return [
      {
        id: section.id,
        label: section.label,
        description: section.description,
        icon: section.icon,
        defaultExpanded: section.defaultExpanded,
        children: pages,
      },
    ];
  });

/** The page's tabs this admin may open, in order (F-211, F-212). */
export const getAdminNavPageTabs = (
  page: AdminNavPage,
  args?: AdminRouteAccessArgs,
): Array<AdminNavTab & { path: string }> =>
  (page.tabs ?? []).flatMap((item) => {
    const route = getRoute(item.routeId);
    return route && evaluateAdminRouteAccess(route, args).canAccess
      ? [{ ...item, path: route.path }]
      : [];
  });

/**
 * True when a route shows the page itself (its own route, its fallback or an
 * alias address), not a member such as a record or tool.
 */
export const isAdminNavPageOwnRoute = (
  page: AdminNavPage,
  routeId: string,
): boolean =>
  routeId === page.routeId ||
  routeId === page.fallbackRouteId ||
  (page.aliasRouteIds ?? []).includes(routeId);

/**
 * Section › Page › Tab (F-019). A hidden member route (a record, a tool)
 * ends with its own label after its page; a page with no tabs stops there.
 * The page crumb links to the page's address for this admin, and is plain
 * text when none is open to them.
 */
export const getAdminNavBreadcrumbs = (
  pathname: string,
  args?: AdminRouteAccessArgs,
): AdminNavBreadcrumb[] => {
  const location = findAdminNavLocation(pathname);
  if (!location) return [];
  const { page, route, section, tab } = location;
  // The section crumb is plain text: which page it opens depends on access.
  const crumbs: AdminNavBreadcrumb[] =
    section.id === "dashboard" ? [] : [{ label: section.label }];
  if (!page) return [...crumbs, { label: route.label }];
  crumbs.push({
    label: page.label,
    path: getAdminNavPageLandingPath(page, args),
  });
  if (tab) {
    if (page.tabs && page.tabs.length > 1) crumbs.push({ label: tab.label });
  } else if (!isAdminNavPageOwnRoute(page, route.id)) {
    crumbs.push({ label: route.label });
  }
  return crumbs;
};

/**
 * Every page and tab by name, plus search-only pages (Nav PRD §4, F-202).
 * Hidden member routes and unhomed routes are never listed.
 */
export const getAdminNavSearchEntries = (
  args?: AdminRouteAccessArgs,
): AdminNavSearchEntry[] => {
  const entries: AdminNavSearchEntry[] = [];
  for (const section of ADMIN_NAV_SECTIONS) {
    for (const page of section.pages) {
      const landingPath = getAdminNavPageLandingPath(page, args);
      if (!landingPath) continue;
      entries.push({
        id: page.id,
        label: page.label,
        location: section.label,
        path: landingPath,
        routeId: page.routeId,
      });
      for (const item of getAdminNavPageTabs(page, args)) {
        // A tab named like its page (Help videos › Help videos) is the page.
        if (item.label === page.label) continue;
        entries.push({
          id: `${page.id}.${item.id}`,
          label: item.label,
          location: `${section.label} › ${page.label}`,
          // F-213: an in-page tab's address is its page plus `?tab=<id>`.
          path: item.inPage
            ? `${landingPath}?${item.addressQuery ?? `tab=${item.id}`}`
            : item.path,
          routeId: item.routeId,
        });
      }
    }
  }
  for (const entry of ADMIN_NAV_SEARCH_ONLY) {
    const route = getRoute(entry.routeId);
    const section = ADMIN_NAV_SECTIONS.find(
      (item) => item.id === entry.sectionId,
    );
    if (!route || !section || !evaluateAdminRouteAccess(route, args).canAccess)
      continue;
    entries.push({
      id: entry.routeId,
      label: entry.label,
      location: section.label,
      path: route.path,
      routeId: entry.routeId,
    });
  }
  return entries;
};
